/**
 * + New sheet (design "Modals") — AI create box (prompt → draft) + a manual
 * grid of document types. Calls back to the shell to open the editor.
 */
import React, { useState } from 'react';
import { Platform, Pressable, Text, TextInput, View } from 'react-native';
import ModalShell from './modalShell';
import { useTheme } from '../../context/themeContext';
import SkIcon from '../skIcon';
import { showToast } from '../skToast';

interface Props {
  visible: boolean;
  onClose: () => void;
  onCreateInvoice: (ai: boolean) => void;
}

const TILES = [
  { act: 'invoice', label: 'Invoice', icon: 'fileText', tone: 'accent' },
  { act: 'quote', label: 'Quote', icon: 'quote', tone: 'info' },
  { act: 'credit-note', label: 'Credit Note', icon: 'creditNote', tone: 'success' },
  { act: 'bill', label: 'Bill', icon: 'cart', tone: 'warn' },
  { act: 'expense', label: 'Expense', icon: 'receipt', tone: 'neutral' },
  { act: 'upload', label: 'Upload receipt', icon: 'upload', tone: 'muted' },
];

const NewSheet: React.FC<Props> = ({ visible, onClose, onCreateInvoice }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [prompt, setPrompt] = useState('');

  const toneColor = (t: string) => ({ accent: [colors.accentSoft, colors.accent], info: [colors.infoSoft, colors.info], success: [colors.successSoft, colors.success], warn: [colors.warnSoft, colors.warn], neutral: [colors.neutralSoft, colors.neutral], muted: [colors.paper3, colors.ink2] }[t] || [colors.paper3, colors.ink2]);

  const pick = (act: string) => {
    if (act === 'invoice') { onClose(); onCreateInvoice(false); return; }
    if (act === 'upload') { onClose(); showToast('info', 'Receipt uploaded', 'AI extracted a draft bill — review low-confidence fields'); return; }
    onClose();
    showToast('info', 'Draft started', `New ${act.replace('-', ' ')} — same editor pattern`);
  };

  return (
    <ModalShell visible={visible} onClose={onClose} title="Create new" width={560}>
      <View style={{ gap: 16 }}>
        {/* AI create box */}
        <View style={{ padding: 14, borderRadius: 8, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: colors.accentInk }}>✦ Create with AI</Text>
            <View style={{ paddingHorizontal: 6, paddingVertical: 1, borderRadius: 999, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.accentLine }}>
              <Text style={{ fontSize: 9, color: colors.accentInk }}>draft only</Text>
            </View>
          </View>
          <TextInput
            value={prompt}
            onChangeText={setPrompt}
            multiline
            placeholder="Describe the invoice — e.g. bill a customer ₹45,000 for a monthly retainer, due in 15 days"
            placeholderTextColor={colors.ink4}
            style={{ minHeight: 56, borderWidth: 1, borderColor: colors.accentLine, backgroundColor: colors.card, borderRadius: 5, padding: 10, fontFamily: font.sans, fontSize: 13, color: colors.ink, textAlignVertical: 'top', ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}) }}
          />
          <Pressable onPress={() => { onClose(); onCreateInvoice(true); }} style={{ marginTop: 10, alignSelf: 'flex-start', height: 32, paddingHorizontal: 14, borderRadius: 5, backgroundColor: colors.accent, justifyContent: 'center' }}>
            <Text style={{ fontFamily: font.sans, fontSize: 13, fontWeight: '500', color: '#fff' }}>Generate draft</Text>
          </Pressable>
        </View>

        {/* manual grid */}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -5 }}>
          {TILES.map((t) => {
            const [bg, fg] = toneColor(t.tone);
            return (
              <View key={t.act} style={{ width: '33.33%', padding: 5 }}>
                <Pressable onPress={() => pick(t.act)} style={{ alignItems: 'center', paddingVertical: 16, borderWidth: 1, borderColor: colors.line, borderRadius: 8, backgroundColor: colors.card, gap: 8 }}>
                  <View style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: bg, alignItems: 'center', justifyContent: 'center' }}>
                    <SkIcon name={t.icon} size={16} color={fg} strokeWidth={2} />
                  </View>
                  <Text style={{ fontFamily: font.sans, fontSize: 12, fontWeight: '500', color: colors.ink2 }}>{t.label}</Text>
                </Pressable>
              </View>
            );
          })}
        </View>
      </View>
    </ModalShell>
  );
};

export default NewSheet;
